import React from "react";
import { Link } from "react-router-dom";
import Breadcrumb from "../components/breadcrumb/Breadcrumb";
import kidsImage from "../assets/img/kids.webp";
import "./digitalSkillsStem.css";

const learningAreas = [
  {
    icon: "fas fa-laptop-code",
    title: "Digital foundations",
    text: "Build confidence with devices, online tools, productivity software, digital safety, and responsible technology use.",
  },
  {
    icon: "fas fa-flask",
    title: "STEM exploration",
    text: "Turn questions into experiments through science, technology, engineering, and mathematics activities that make learning tangible.",
  },
  {
    icon: "fas fa-lightbulb",
    title: "Creative problem-solving",
    text: "Learn to break down challenges, test ideas, learn from mistakes, and design solutions that respond to real needs.",
  },
  {
    icon: "fas fa-users",
    title: "Collaboration and confidence",
    text: "Practice communication, teamwork, presentation, and leadership while creating projects with peers and mentors.",
  },
];

const outcomes = [
  "Use digital tools thoughtfully and independently",
  "Understand how technology connects to everyday problems",
  "Develop curiosity through projects, experiments, and questions",
  "Communicate ideas clearly and work effectively in teams",
  "Build a foundation for future study and technology careers",
];

const learningFormats = [
  {
    icon: "fas fa-video",
    title: "Evening Online Coding Classes",
    text: "Live, interactive sessions guided by experienced instructors.",
  },
  {
    icon: "fas fa-tools",
    title: "Weekend Practical Bootcamps",
    text: "Hands-on workshops where students build real-world games, websites, and mobile apps.",
  },
];

const DigitalSkillsStem = () => {
  return (
    <>
      <Breadcrumb current="Digital Skills & STEM Education" />

      <main className="stem-page">
        <section className="stem-hero" aria-labelledby="stem-page-title">
          <div className="container">
            <div className="row align-items-center g-5">
              <div className="col-lg-7">
                <span className="stem-kicker">Learning for the future</span>
                <h1 id="stem-page-title">Digital Skills & STEM Education</h1>
                <p className="stem-lead">
                  We help young people and curious learners become confident creators,
                  problem-solvers, and responsible users of technology.
                </p>
                <div className="d-flex flex-wrap gap-3">
                  <Link className="theme-btn" to="/signup">
                    Join a program <i className="fas fa-arrow-right ms-2" aria-hidden="true"></i>
                  </Link>
                  <Link className="stem-text-link" to="/contact">
                    Talk to our team <i className="fas fa-comment-dots ms-2" aria-hidden="true"></i>
                  </Link>
                </div>
              </div>
              <div className="col-lg-5">
                <div className="stem-hero-image">
                  <img src={kidsImage} alt="Learners exploring technology together" />
                  <div className="stem-stat">
                    <strong>Learn by doing</strong>
                    <span>Projects that make ideas real</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="stem-intro" aria-labelledby="stem-approach-title">
          <div className="container">
            <div className="row g-5 align-items-start">
              <div className="col-lg-5">
                <span className="stem-kicker">Our approach</span>
                <h2 id="stem-approach-title">Skills that open doors</h2>
              </div>
              <div className="col-lg-7">
                <p>
                  Digital literacy is more than knowing how to use a device. It is the
                  ability to think critically, create with technology, stay safe online,
                  and use knowledge to make a positive difference.
                </p>
                <p className="mb-0">
                  Our STEM learning experiences connect classroom concepts to practical
                  challenges. Learners ask questions, build prototypes, test solutions,
                  and reflect on what they discover along the way.
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="stem-formats" aria-labelledby="learning-formats-title">
          <div className="container">
            <div className="stem-section-heading">
              <span className="stem-kicker">Flexible ways to learn</span>
              <h2 id="learning-formats-title">Choose the format that fits your schedule</h2>
            </div>
            <div className="row g-4">
              {learningFormats.map((format) => (
                <div className="col-md-6" key={format.title}>
                  <article className="stem-format">
                    <i className={`${format.icon} stem-format-icon`} aria-hidden="true"></i>
                    <div>
                      <h3>{format.title}</h3>
                      <p>{format.text}</p>
                    </div>
                  </article>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="stem-areas" aria-labelledby="learning-areas-title">
          <div className="container">
            <div className="stem-section-heading">
              <span className="stem-kicker">What we develop</span>
              <h2 id="learning-areas-title">A strong foundation for what comes next</h2>
            </div>
            <div className="row g-4">
              {learningAreas.map((area) => (
                <div className="col-md-6" key={area.title}>
                  <article className="stem-area">
                    <i className={`${area.icon} stem-area-icon`} aria-hidden="true"></i>
                    <div>
                      <h3>{area.title}</h3>
                      <p>{area.text}</p>
                    </div>
                  </article>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="stem-outcomes" aria-labelledby="outcomes-title">
          <div className="container">
            <div className="row g-5 align-items-center">
              <div className="col-lg-5">
                <span className="stem-kicker">Learner outcomes</span>
                <h2 id="outcomes-title">From curiosity to capability</h2>
                <p>
                  Every activity is designed to leave learners with something they can
                  explain, demonstrate, improve, or apply in the real world.
                </p>
              </div>
              <div className="col-lg-7">
                <ul className="stem-outcome-list">
                  {outcomes.map((outcome) => (
                    <li key={outcome}>
                      <i className="fas fa-check-circle" aria-hidden="true"></i>
                      <span>{outcome}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>

        <section className="stem-cta" aria-labelledby="stem-cta-title">
          <div className="container">
            <div className="stem-cta-inner">
              <div>
                <span className="stem-kicker">Start building</span>
                <h2 id="stem-cta-title">Give learners the tools to shape tomorrow.</h2>
              </div>
              <Link className="theme-btn" to="/signup">Register now</Link>
            </div>
          </div>
        </section>
      </main>
    </>
  );
};

export default DigitalSkillsStem;
